import React from 'react';
import _get from 'lodash/get';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';
import { getEventPageName } from 'utils/trackingPageUtils';

const useStyles = makeStyles(theme => ({
  container: {
    zIndex: 9998,
    boxShadow: '0px 0px 14px 0px #00000033',
    maxWidth: 450,
    borderRadius: 20,
    backgroundColor: 'white',
    position: 'fixed',
    width: 'auto',
    top: 40,
    right: 40,
    [theme.breakpoints.down('xs')]: {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      right: 'auto',
      width: '90%',
    },
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
  },
  header: {
    fontWeight: 600,
    fontSize: 22,
    color: theme.palette.text.primary,
    fontFamily: 'Poppins',
  },
  content: {
    padding: '39px 50px',
    paddingBottom: 12,
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: '19px 25px 24px',
    },
  },
  description: {
    marginTop: 20,
    fontSize: 20,
    color: theme.palette.text.primary,
  },
  cta: {
    width: '100%',
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 6,
    padding: 14,
    borderRadius: 8,
  },
  signUpRow: {
    marginTop: 15,
    fontSize: 14,
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      marginTop: 14,
      fontSize: 14,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  signUpLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    fontWeight: 600,
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'nowrap',
    },
  },
}));

export default function EmailLoginReminderDialog({
  open,
  onClose,
  onLogin,
  onSignUp,
}) {
  const classes = useStyles();
  const [signInSiteCopy] = useSiteCopySelector(['sign-in']);
  const emailReminderCopy = signInSiteCopy?.pageCopy?.emailLoginReminder || {};
  const {
    welcomeBackHeader,
    signInPrompt,
    benefitsDescription,
    loginButtonLabel,
  } = emailReminderCopy;
  const notMemberYetText = _get(
    emailReminderCopy,
    'notMemberYetText',
    'Not a member yet?',
  );
  const signUpLinkLabel = _get(emailReminderCopy, 'signUpLinkLabel', 'Sign Up');
  if (!open) return null;

  const handleLogin = () => {
    Mixpanel.track('Login Reminder - Clicked', {
      page: getEventPageName(),
    });
    onLogin();
  };

  const handleClose = () => {
    Mixpanel.track('Login Reminder - Dismissed', {
      page: getEventPageName(),
    });
    onClose();
  };

  const handleSignUpClick = () => {
    Mixpanel.track('Login Reminder - Sign Up Clicked', {
      page: getEventPageName(),
    });
    if (onSignUp) onSignUp();
  };

  return (
    <Grid container justifyContent="center" className={classes.container}>
      <IconButton
        className={classes.closeButton}
        onClick={handleClose}
        aria-label="Close"
        size="small"
      >
        <CloseIcon />
      </IconButton>
      <Grid item xs={12} className={classes.content}>
        <Typography className={classes.header}>{welcomeBackHeader}</Typography>
        <Typography className={classes.header}>{signInPrompt}</Typography>
        <Typography className={classes.description}>
          {benefitsDescription}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          className={classes.cta}
          onClick={handleLogin}
        >
          {loginButtonLabel}
        </Button>
        <Typography className={classes.signUpRow} component="span">
          {notMemberYetText}{' '}
          <span
            role="button"
            tabIndex={0}
            className={classes.signUpLink}
            onClick={handleSignUpClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSignUpClick();
              }
            }}
          >
            {signUpLinkLabel}
          </span>
        </Typography>
      </Grid>
    </Grid>
  );
}
